var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
var __decorateParam = (index, decorator) => (target, key) => decorator(target, key, index);

// db/a-roles.ts
import { A } from "@adornis/base/env-info";
import { Arg, Entity, Field, Query } from "@adornis/baseql/decorators";
import "@adornis/baseql/entities/adornisEntity";
import { MongoEntity } from "@adornis/baseql/entities/mongoEntity";
import { getCollection } from "@adornis/baseql/server/collections";
import { selectionSet } from "@adornis/baseql/utils/queryGeneration";
import { validate } from "@adornis/validation/decorators";
import { and } from "@adornis/validation/functions/and";
import { nonOptional } from "@adornis/validation/functions/nonOptional";
import { AdornisUser } from "./a-user";
import { AdornisPermissionType } from "./types";
import { nameValidation } from "./validators";
var GLOBAL_CONTEXT = "GLOBAL";
var AdornisRoles = class extends MongoEntity {
  static get allFields() {
    return selectionSet(() => AdornisRoles, 2);
  }
  static async checkRole(role, context) {
    if (!A.isClient)
      throw new Error("checkRole(): Clientside Function");
    const user = await AdornisUser.currentUser.result;
    if (!user)
      return false;
    if (user.roles.findIndex((x) => x.name === role && x.contexts.includes(context)) === -1)
      return false;
    else
      return true;
  }
  static async checkPermission(permission, roleContexts) {
    if (!A.isClient)
      throw new Error("checkPermission(): Clientside Function");
    const user = await AdornisUser.currentUser.result;
    if (!user)
      return false;
    return await this.checkUserPermission(user._id, permission, roleContexts)("");
  }
  static optimisticallyCheckPermission(user, permission, context) {
    return user.roles.some(
      (role) => (role.contexts.includes(context) || role.contexts.includes(GLOBAL_CONTEXT)) && role.permissions.includes(permission)
    );
  }
  static checkUserRole(userId, roleName, context = GLOBAL_CONTEXT) {
    return async (gqlFields) => {
      const collection = await getCollection(AdornisUser._collectionName);
      let user;
      user = await collection.findOne({ _id: userId }, { projection: { password: 0 } });
      if (!user)
        throw new Error(`checkUserRole(): Invalid userId ${userId}.`);
      if (user.roles?.findIndex((x) => x.name === roleName && x.contexts?.includes(context)) === -1)
        return false;
      else
        return true;
    };
  }
  static checkUserPermission(userId, permission, roleContexts = [GLOBAL_CONTEXT]) {
    return async (gqlFields) => {
      const user = await AdornisUser.getByID(userId)(selectionSet(() => AdornisUser, 3, ["password"]));
      if (!user)
        throw new Error(`checkUserRole(): Invalid userId ${userId}.`);
      const rolesCollection = await getCollection("adornisroles");
      const userRoles = await rolesCollection.find({
        name: {
          $in: user.roles.filter((role) => roleContexts.some((context) => role.contexts.includes(context))).map((role) => role.name)
        }
      }).toArray();
      if (!userRoles || !userRoles.length)
        return false;
      let hasPermission = false;
      await Promise.all(
        userRoles.map(async (role) => {
          const permissionObj = role.permissions.find((perm) => perm.name === permission);
          if (!permissionObj)
            return;
          if (!permissionObj.hasValidationHook) {
            hasPermission = true;
            return;
          }
          const validationHooks = AdornisRoles.validationHookRegister.filter(
            (mw) => mw.permission === permission && mw.role === role.name
          );
          console.log("middleWares: ", validationHooks, permission, role.name, AdornisRoles.validationHookRegister);
          await Promise.all(
            validationHooks.map(async (mw) => {
              if (!await mw.validationHook(user)) {
                hasPermission = false;
                return;
              }
              hasPermission = true;
            })
          );
        })
      );
      return hasPermission;
    };
  }
  static checkRolePermission(roleName, permission) {
    return async (gqlFields) => {
      const rolesCollection = await getCollection(AdornisRoles._collectionName);
      return !!await rolesCollection.findOne({ name: roleName, "permissions.name": permission });
    };
  }
};
AdornisRoles._collectionName = "adornisroles";
AdornisRoles._class = "AdornisRoles";
AdornisRoles.validationHookRegister = [];
__decorateClass([
  validate(and(nonOptional(), nameValidation)),
  Field((type) => String)
], AdornisRoles.prototype, "name", 2);
__decorateClass([
  validate(nonOptional()),
  Field((type) => [AdornisPermissionType])
], AdornisRoles.prototype, "permissions", 2);
__decorateClass([
  Query((type) => Boolean),
  __decorateParam(0, Arg("userId", (type) => String)),
  __decorateParam(1, Arg("roleName", (type) => String)),
  __decorateParam(2, Arg("context", (type) => String))
], AdornisRoles, "checkUserRole", 1);
__decorateClass([
  Query((type) => Boolean),
  __decorateParam(0, Arg("userId", (type) => String)),
  __decorateParam(1, Arg("permission", (type) => String)),
  __decorateParam(2, Arg("roleContexts", (type) => [String]))
], AdornisRoles, "checkUserPermission", 1);
__decorateClass([
  Query((type) => Boolean),
  __decorateParam(0, Arg("roleName", (type) => String)),
  __decorateParam(1, Arg("permission", (type) => String))
], AdornisRoles, "checkRolePermission", 1);
AdornisRoles = __decorateClass([
  Entity()
], AdornisRoles);
export {
  AdornisRoles,
  GLOBAL_CONTEXT
};
